import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, Building2, MapPin, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../api";
import EventDayGroups from "../components/EventDayGroups";
import { applyMeta } from "../lib/meta";

// Small month calendar highlighting days that have events.
function MonthCalendar({ eventsByDay }) {
  const firstEventMonth = useMemo(() => {
    const keys = Object.keys(eventsByDay).sort();
    return keys.length ? new Date(keys[0] + "T00:00") : new Date();
  }, [eventsByDay]);
  const [cursor, setCursor] = useState(() => new Date(firstEventMonth.getFullYear(), firstEventMonth.getMonth(), 1));
  useEffect(() => { setCursor(new Date(firstEventMonth.getFullYear(), firstEventMonth.getMonth(), 1)); }, [firstEventMonth]);

  const year = cursor.getFullYear(), month = cursor.getMonth();
  const monthLabel = cursor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const key = (d) => `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="venue-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="cal-prev" className="text-white/60 hover:text-white p-1"><ChevronLeft size={18} /></button>
        <span className="text-white font-semibold">{monthLabel}</span>
        <button onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="cal-next" className="text-white/60 hover:text-white p-1"><ChevronRight size={18} /></button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-[#8a97a8] mb-1">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const has = eventsByDay[key(d)];
          return (
            <div key={i} className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm ${has ? "bg-[#2ec9c9]/20 text-white font-semibold" : "text-[#6b7a90]"}`}>
              {d}
              {has && <span className="w-1.5 h-1.5 rounded-full bg-[#2ec9c9] mt-0.5" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function VenuePage() {
  const { citySlug, venueSlug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/venues/${citySlug}/${venueSlug}`).then(({ data }) => {
      setData(data);
      applyMeta({
        title: `${data.venue.name}, ${data.venue.city} — what's on · TicketsPro.app`,
        description: `Upcoming events at ${data.venue.name} in ${data.venue.city}. Browse the calendar and book tickets on TicketsPro.`,
        image: data.venue.image_url,
      });
    }).catch(() => setErr(true));
  }, [citySlug, venueSlug]);

  const v = data?.venue;
  const eventsByDay = useMemo(() => {
    const m = {};
    (data?.events || []).forEach((e) => { const k = (e.event_date || "").slice(0, 10); if (k) m[k] = (m[k] || 0) + 1; });
    return m;
  }, [data]);
  const mapQuery = v ? encodeURIComponent([v.name, v.address, v.city].filter(Boolean).join(", ")) : "";

  return (
    <div className="min-h-screen bg-[#0d1626]" data-testid="venue-page">
      <div className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={v ? `/city/${v.city_slug}` : "/"} className="text-white/80 hover:text-white flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> {v ? v.city : "Home"}</Link>
          <span className="text-white font-bold">TicketsPro.app</span>
        </div>
      </div>

      {err ? (
        <div className="max-w-5xl mx-auto px-6 py-24 text-center" data-testid="venue-not-found">
          <p className="text-[#8a97a8]">We couldn't find that venue.</p>
          <Link to="/events" className="inline-block mt-4 text-[#2ec9c9] hover:underline">Browse all events</Link>
        </div>
      ) : data === null ? (
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={32} /></div>
      ) : (
        <>
          <div className="relative overflow-hidden border-b border-white/10 min-h-[240px] flex items-end"
            style={{ background: "linear-gradient(135deg, #13303a, #0d1626 70%)" }}>
            {v.image_url && <img src={v.image_url} alt={v.name} className="absolute inset-0 w-full h-full object-cover opacity-60" />}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0d1626 10%, rgba(13,22,38,0.35) 70%)" }} />
            <div className="relative max-w-5xl mx-auto px-6 py-10 w-full">
              <div className="flex items-center gap-3">
                <span className="w-12 h-12 rounded-xl flex items-center justify-center bg-[#3b82f6]/20 text-[#93c5fd] shrink-0"><Building2 size={24} /></span>
                <div>
                  <p className="text-sm uppercase tracking-wide text-[#93c5fd] flex items-center gap-1.5"><MapPin size={13} /> {v.city}{v.address ? ` · ${v.address}` : ""}</p>
                  <h1 className="text-3xl sm:text-5xl font-bold text-white">{v.name}</h1>
                  {v.capacity ? <p className="text-sm text-[#8a97a8] mt-1">Capacity {v.capacity}</p> : null}
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 order-2 lg:order-1">
              <h2 className="text-white font-semibold text-lg mb-4 flex items-center gap-2"><CalendarDays size={18} className="text-[#2ec9c9]" /> What's on here</h2>
              {(data.events || []).length === 0 ? (
                <div className="py-12 text-center rounded-2xl border border-white/10" data-testid="venue-empty">
                  <p className="text-[#8a97a8]">No upcoming events at {v.name} right now.</p>
                  <Link to="/events" className="inline-block mt-4 text-[#2ec9c9] hover:underline">Browse all events</Link>
                </div>
              ) : (
                <EventDayGroups events={data.events} testidPrefix="venue" />
              )}
            </div>
            <div className="order-1 lg:order-2 space-y-5">
              <MonthCalendar eventsByDay={eventsByDay} />
              {mapQuery && (
                <div className="rounded-2xl overflow-hidden border border-white/10" data-testid="venue-map">
                  <iframe title="venue-map" width="100%" height="220" loading="lazy" style={{ border: 0 }}
                    src={`https://www.google.com/maps?q=${mapQuery}&output=embed`} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
